import React, { useState } from 'react';
import { Card, List, Button, Space, Modal, Input, Dropdown } from 'antd';
import { FolderOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ShareAltOutlined } from '@ant-design/icons';
import transcript_pb from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';

export type CollectionsListProps = {
  collections: transcript_pb.Collection.AsObject[];
  onCollectionClick: (collection: transcript_pb.Collection.AsObject) => void;
  onCreateCollection: (displayName: string) => void;
  onRenameCollection?: (collection: transcript_pb.Collection.AsObject, newDisplayName: string) => void;
  onDeleteCollection?: (collection: transcript_pb.Collection.AsObject) => void;
  onShareCollection?: (collection: transcript_pb.Collection.AsObject) => void;
  loading?: boolean;
  selectedCollectionName?: string;
};

const CollectionsList: React.FC<CollectionsListProps> = ({
  collections,
  onCollectionClick,
  onCreateCollection,
  onRenameCollection,
  onDeleteCollection,
  onShareCollection,
  loading,
  selectedCollectionName,
}) => {
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [collectionToManage, setCollectionToManage] = useState<transcript_pb.Collection.AsObject | null>(null);

  const handleCreate = () => {
    if (newCollectionName.trim()) {
      onCreateCollection(newCollectionName.trim());
      setNewCollectionName('');
      setIsCreateModalVisible(false);
    }
  };

  const handleRename = () => {
    if (newCollectionName.trim() && collectionToManage && onRenameCollection) {
      onRenameCollection(collectionToManage, newCollectionName.trim());
      setNewCollectionName('');
      setCollectionToManage(null);
      setIsRenameModalVisible(false);
    }
  };

  const handleDelete = () => {
    if (collectionToManage && onDeleteCollection) {
      onDeleteCollection(collectionToManage);
      setCollectionToManage(null);
      setIsDeleteModalVisible(false);
    }
  };

  const showRenameModal = (collection: transcript_pb.Collection.AsObject) => {
    setCollectionToManage(collection);
    setNewCollectionName(collection.displayName || collection.name);
    setIsRenameModalVisible(true);
  };

  const showDeleteModal = (collection: transcript_pb.Collection.AsObject) => {
    setCollectionToManage(collection);
    setIsDeleteModalVisible(true);
  };

  return (
    <>
      <Card
        title={
          <Space>
            <FolderOutlined />
            <span>Söfn</span>
          </Space>
        }
        extra={
          <Button type='primary' size='small' icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)}>
            Nýtt safn
          </Button>
        }
        bodyStyle={{ padding: 0 }}
        style={{ marginBottom: 16 }}
      >
        <List
          loading={loading}
          dataSource={collections}
          renderItem={(item) => {
            const menuItems = [];
            if (onShareCollection) {
              menuItems.push({
                key: 'share',
                icon: <ShareAltOutlined />,
                label: 'Deila safni',
                onClick: () => onShareCollection(item),
              });
            }
            if (onRenameCollection) {
              menuItems.push({
                key: 'rename',
                icon: <EditOutlined />,
                label: 'Endurnefna safn',
                onClick: () => showRenameModal(item),
              });
            }
            if (onDeleteCollection) {
              menuItems.push({
                key: 'delete',
                danger: true,
                icon: <DeleteOutlined />,
                label: 'Eyða safni',
                onClick: () => showDeleteModal(item),
              });
            }

            const isSelected = item.name === selectedCollectionName;

            return (
              <Dropdown trigger={['contextMenu']} menu={{ items: menuItems }}>
                <List.Item
                  style={{
                    cursor: 'pointer',
                    padding: '12px 24px',
                    backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                    borderRight: isSelected ? '3px solid #1890ff' : 'none',
                  }}
                  onClick={() => onCollectionClick(item)}
                >
                  <Space>
                    <FolderOutlined style={{ color: isSelected ? '#1890ff' : undefined }} />
                    <span
                      style={{ fontWeight: isSelected ? 'bold' : 'normal', color: isSelected ? '#1890ff' : undefined }}
                    >
                      {item.displayName || item.name}
                    </span>
                  </Space>
                </List.Item>
              </Dropdown>
            );
          }}
          locale={{ emptyText: 'Engin söfn fundust' }}
        />
      </Card>

      <Modal
        title='Búa til nýtt safn'
        open={isCreateModalVisible}
        onOk={handleCreate}
        onCancel={() => setIsCreateModalVisible(false)}
        okText='Búa til'
        cancelText='Hætta við'
        okButtonProps={{ disabled: !newCollectionName.trim() }}
      >
        <Input
          placeholder='Nafn á safni'
          value={newCollectionName}
          onChange={(e) => setNewCollectionName(e.target.value)}
          onPressEnter={handleCreate}
          autoFocus
        />
      </Modal>

      <Modal
        title='Endurnefna safn'
        open={isRenameModalVisible}
        onOk={handleRename}
        onCancel={() => setIsRenameModalVisible(false)}
        okText='Breyta'
        cancelText='Hætta við'
        okButtonProps={{ disabled: !newCollectionName.trim() }}
      >
        <Input
          placeholder='Nýtt nafn'
          value={newCollectionName}
          onChange={(e) => setNewCollectionName(e.target.value)}
          onPressEnter={handleRename}
          autoFocus
        />
      </Modal>

      <Modal
        title='Eyða safni'
        open={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText='Já, eyða'
        cancelText='Hætta við'
        okButtonProps={{ danger: true }}
      >
        <p>Ertu viss um að þú viljir eyða þessu safni?</p>
        <p>
          Upptökum í safninu verður <strong>ekki</strong> eytt.
        </p>
      </Modal>
    </>
  );
};

export default CollectionsList;
